import React, { useCallback, useEffect, useState } from 'react';
import {
  getFreightZoneRates,
  updateFreightZoneRate,
  updateFreightDefaultZone,
  getFreightPostcodeZones,
  createFreightPostcodeZone,
  updateFreightPostcodeZone,
  deleteFreightPostcodeZone,
} from '../../services/syncApi';
import FreightOverrides from './FreightOverrides';

// "Ship To Addresses" tab: configure how each B2B company address gets its
// Type / FSTD / FEXP freight extra fields. Type is derived per address from the
// resolved country + postcode (Metro wins on overlap, then Regional, then the
// per-country fallback). POA is a manual per-address override (see below).

const COUNTRIES = ['AU', 'NZ'];
const RATE_ZONES = ['metro', 'regional', 'poa'];
const RANGE_ZONES = ['metro', 'regional'];
const ZONE_LABEL = { metro: 'Metro', regional: 'Regional', poa: 'POA' };

const errToText = (raw) => {
  if (!raw) return '';
  if (typeof raw === 'string') return raw;
  if (raw.message) return raw.message;
  if (Array.isArray(raw)) return raw.map((r) => r.msg || JSON.stringify(r)).join('; ');
  try { return JSON.stringify(raw); } catch { return String(raw); }
};
const formatErr = (e) => errToText(e?.response?.data?.detail || e?.message || 'request failed');

const numOrNull = (v) => {
  if (v === '' || v === null || v === undefined) return null;
  const n = Number(v);
  return Number.isNaN(n) ? null : n;
};
const rateStr = (v) => (v === null || v === undefined ? '' : String(v));

// ── Zone rates + per-country fallback ──
const ZoneRatesSection = ({ onError }) => {
  const [rates, setRates] = useState({}); // `${country}|${zone}` -> {std, exp}
  const [fallbacks, setFallbacks] = useState({}); // country -> zone
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getFreightZoneRates();
      const r = {};
      (data.rates || []).forEach((row) => {
        r[`${row.country}|${row.zone}`] = {
          std: rateStr(row.freight_standard),
          exp: rateStr(row.freight_express),
        };
      });
      const f = {};
      (data.fallbacks || []).forEach((row) => { f[row.country] = row.default_freight_zone; });
      setRates(r);
      setFallbacks(f);
    } catch (e) {
      onError(formatErr(e));
    } finally {
      setLoading(false);
    }
  }, [onError]);

  useEffect(() => { load(); }, [load]);

  const setRate = (country, zone, field, value) =>
    setRates((prev) => ({
      ...prev,
      [`${country}|${zone}`]: { ...prev[`${country}|${zone}`], [field]: value },
    }));

  const saveRate = async (country, zone) => {
    const key = `${country}|${zone}`;
    setSavingKey(key);
    onError(null);
    try {
      const draft = rates[key] || {};
      const updated = await updateFreightZoneRate(country, zone, {
        freight_standard: numOrNull(draft.std),
        freight_express: numOrNull(draft.exp),
      });
      setRates((prev) => ({
        ...prev,
        [key]: { std: rateStr(updated.freight_standard), exp: rateStr(updated.freight_express) },
      }));
    } catch (e) {
      onError(formatErr(e));
    } finally {
      setSavingKey(null);
    }
  };

  const saveFallback = async (country, zone) => {
    onError(null);
    try {
      await updateFreightDefaultZone(country, { default_freight_zone: zone });
      setFallbacks((prev) => ({ ...prev, [country]: zone }));
    } catch (e) {
      onError(formatErr(e));
    }
  };

  return (
    <section className="card">
      <h2>Zone rates &amp; fallback</h2>
      <p className="muted">
        Standard (<code>FSTD</code>) and express (<code>FEXP</code>) rates per country and zone.
        Leave a rate blank to omit that field (e.g. Regional express, POA). The fallback zone
        applies to postcodes that match no range below.
      </p>
      {loading ? (
        <p className="muted">Loading…</p>
      ) : (
        COUNTRIES.map((country) => (
          <div key={country} style={{ marginBottom: '1.25rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.4rem' }}>
              <strong>{country}</strong>
              <label className="muted" style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                Fallback zone
                <select
                  value={fallbacks[country] || 'regional'}
                  onChange={(e) => saveFallback(country, e.target.value)}
                >
                  {RATE_ZONES.map((z) => (
                    <option key={z} value={z}>{ZONE_LABEL[z]}</option>
                  ))}
                </select>
              </label>
            </div>
            <table className="value-lookups-table">
              <thead>
                <tr>
                  <th>Zone</th>
                  <th>FSTD (standard)</th>
                  <th>FEXP (express)</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {RATE_ZONES.map((zone) => {
                  const key = `${country}|${zone}`;
                  const draft = rates[key] || { std: '', exp: '' };
                  return (
                    <tr key={key}>
                      <td>{ZONE_LABEL[zone]}</td>
                      <td>
                        <input
                          type="number" min="0" step="0.01"
                          value={draft.std}
                          placeholder="—"
                          onChange={(e) => setRate(country, zone, 'std', e.target.value)}
                          style={{ width: '7rem' }}
                        />
                      </td>
                      <td>
                        <input
                          type="number" min="0" step="0.01"
                          value={draft.exp}
                          placeholder="—"
                          onChange={(e) => setRate(country, zone, 'exp', e.target.value)}
                          style={{ width: '7rem' }}
                        />
                      </td>
                      <td>
                        <button
                          type="button" className="primary"
                          onClick={() => saveRate(country, zone)}
                          disabled={savingKey === key}
                        >
                          {savingKey === key ? 'Saving…' : 'Save'}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ))
      )}
    </section>
  );
};

const EMPTY_RANGE = { country: 'AU', zone: 'metro', postcode_start: '', postcode_end: '', label: '' };

// ── Postcode -> zone ranges ──
const PostcodeRangesSection = ({ onError }) => {
  const [rows, setRows] = useState([]);
  const [drafts, setDrafts] = useState({}); // id -> editable row
  const [editing, setEditing] = useState(null); // id being edited
  const [adding, setAdding] = useState(EMPTY_RANGE);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [filterCountry, setFilterCountry] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getFreightPostcodeZones(filterCountry || undefined);
      setRows(data);
    } catch (e) {
      onError(formatErr(e));
    } finally {
      setLoading(false);
    }
  }, [onError, filterCountry]);

  useEffect(() => { load(); }, [load]);

  const startEdit = (row) => {
    setEditing(row.id);
    setDrafts((p) => ({ ...p, [row.id]: { ...row } }));
  };

  const editField = (id, field, value) =>
    setDrafts((p) => ({ ...p, [id]: { ...p[id], [field]: value } }));

  const validRange = (r) => {
    const s = Number(r.postcode_start);
    const e = Number(r.postcode_end);
    if (!Number.isInteger(s) || !Number.isInteger(e)) return 'start/end must be integers';
    if (s < 0 || s > 9999 || e < 0 || e > 9999) return 'postcodes must be 0–9999';
    if (e < s) return 'end must be ≥ start';
    return null;
  };

  const saveEdit = async (id) => {
    const r = drafts[id];
    const err = validRange(r);
    if (err) { onError(err); return; }
    setBusy(true);
    onError(null);
    try {
      await updateFreightPostcodeZone(id, {
        country: r.country, zone: r.zone,
        postcode_start: Number(r.postcode_start), postcode_end: Number(r.postcode_end),
        label: r.label || null, active: r.active,
      });
      setEditing(null);
      await load();
    } catch (e) {
      onError(formatErr(e));
    } finally {
      setBusy(false);
    }
  };

  const toggleActive = async (row) => {
    setBusy(true);
    onError(null);
    try {
      await updateFreightPostcodeZone(row.id, {
        country: row.country, zone: row.zone,
        postcode_start: row.postcode_start, postcode_end: row.postcode_end,
        label: row.label || null, active: !row.active,
      });
      await load();
    } catch (e) {
      onError(formatErr(e));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    setBusy(true);
    onError(null);
    try {
      await deleteFreightPostcodeZone(id);
      await load();
    } catch (e) {
      onError(formatErr(e));
    } finally {
      setBusy(false);
    }
  };

  const addRange = async () => {
    const err = validRange(adding);
    if (err) { onError(err); return; }
    setBusy(true);
    onError(null);
    try {
      await createFreightPostcodeZone({
        country: adding.country, zone: adding.zone,
        postcode_start: Number(adding.postcode_start), postcode_end: Number(adding.postcode_end),
        label: adding.label || null, active: true,
      });
      setAdding(EMPTY_RANGE);
      await load();
    } catch (e) {
      onError(formatErr(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="card">
      <div className="value-lookups-header">
        <h2>Postcode ranges</h2>
        <label className="muted" style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
          Country
          <select value={filterCountry} onChange={(e) => setFilterCountry(e.target.value)}>
            <option value="">All</option>
            {COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </label>
      </div>
      <p className="muted">
        Postcode ranges that map to <strong>Metro</strong> or <strong>Regional</strong>. Single
        postcodes use the same value for start and end. Metro ranges take precedence over Regional
        on overlap.
      </p>

      {loading ? (
        <p className="muted">Loading…</p>
      ) : (
        <table className="value-lookups-table">
          <thead>
            <tr>
              <th>Country</th>
              <th>Zone</th>
              <th>Start</th>
              <th>End</th>
              <th>Label</th>
              <th>Active</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <select value={adding.country} onChange={(e) => setAdding({ ...adding, country: e.target.value })}>
                  {COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </td>
              <td>
                <select value={adding.zone} onChange={(e) => setAdding({ ...adding, zone: e.target.value })}>
                  {RANGE_ZONES.map((z) => <option key={z} value={z}>{ZONE_LABEL[z]}</option>)}
                </select>
              </td>
              <td>
                <input type="number" min="0" max="9999" value={adding.postcode_start}
                  onChange={(e) => setAdding({ ...adding, postcode_start: e.target.value })}
                  style={{ width: '5rem' }} />
              </td>
              <td>
                <input type="number" min="0" max="9999" value={adding.postcode_end}
                  onChange={(e) => setAdding({ ...adding, postcode_end: e.target.value })}
                  style={{ width: '5rem' }} />
              </td>
              <td>
                <input type="text" value={adding.label}
                  onChange={(e) => setAdding({ ...adding, label: e.target.value })}
                  placeholder="e.g. Sydney Metro" style={{ width: '11rem' }} />
              </td>
              <td className="muted">—</td>
              <td>
                <button type="button" className="primary" onClick={addRange} disabled={busy}>Add</button>
              </td>
            </tr>
            {rows.map((row) => {
              const isEditing = editing === row.id;
              const d = drafts[row.id] || row;
              return (
                <tr key={row.id}>
                  {isEditing ? (
                    <>
                      <td>
                        <select value={d.country} onChange={(e) => editField(row.id, 'country', e.target.value)}>
                          {COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
                        </select>
                      </td>
                      <td>
                        <select value={d.zone} onChange={(e) => editField(row.id, 'zone', e.target.value)}>
                          {RANGE_ZONES.map((z) => <option key={z} value={z}>{ZONE_LABEL[z]}</option>)}
                        </select>
                      </td>
                      <td>
                        <input type="number" min="0" max="9999" value={d.postcode_start}
                          onChange={(e) => editField(row.id, 'postcode_start', e.target.value)}
                          style={{ width: '5rem' }} />
                      </td>
                      <td>
                        <input type="number" min="0" max="9999" value={d.postcode_end}
                          onChange={(e) => editField(row.id, 'postcode_end', e.target.value)}
                          style={{ width: '5rem' }} />
                      </td>
                      <td>
                        <input type="text" value={d.label || ''}
                          onChange={(e) => editField(row.id, 'label', e.target.value)}
                          style={{ width: '11rem' }} />
                      </td>
                      <td className="muted">{row.active ? 'Yes' : 'No'}</td>
                      <td>
                        <button type="button" className="primary" onClick={() => saveEdit(row.id)} disabled={busy}>Save</button>{' '}
                        <button type="button" className="ghost" onClick={() => setEditing(null)} disabled={busy}>Cancel</button>
                      </td>
                    </>
                  ) : (
                    <>
                      <td>{row.country}</td>
                      <td>{ZONE_LABEL[row.zone]}</td>
                      <td>{row.postcode_start}</td>
                      <td>{row.postcode_end}</td>
                      <td>{row.label || <span className="muted">—</span>}</td>
                      <td>{row.active ? 'Yes' : <span className="muted">No</span>}</td>
                      <td>
                        <button type="button" className="ghost" onClick={() => startEdit(row)} disabled={busy}>Edit</button>{' '}
                        <button type="button" className="ghost" onClick={() => toggleActive(row)} disabled={busy}>
                          {row.active ? 'Disable' : 'Enable'}
                        </button>{' '}
                        <button type="button" className="ghost" onClick={() => remove(row.id)} disabled={busy}>Delete</button>
                      </td>
                    </>
                  )}
                </tr>
              );
            })}
            {rows.length === 0 && (
              <tr><td colSpan={7} className="muted">No ranges configured.</td></tr>
            )}
          </tbody>
        </table>
      )}
    </section>
  );
};

const ShipToFreightSettings = () => {
  const [error, setError] = useState(null);
  return (
    <div style={{ marginTop: '1rem' }}>
      <p className="muted">
        Pushed per ship-to as BigCommerce address extra fields: <code>ShipToNum</code> (Epicor
        ship-to name), <code>Type</code> (Metro/Regional/POA), <code>FSTD</code> and{' '}
        <code>FEXP</code>. Applied on the next <strong>Address</strong> sync.
      </p>

      {error && (
        <div className="card error-banner">
          <strong>Error:</strong> {error}
          <button type="button" className="ghost" onClick={() => setError(null)}>Dismiss</button>
        </div>
      )}

      <ZoneRatesSection onError={setError} />
      <PostcodeRangesSection onError={setError} />
      <FreightOverrides onError={setError} />
    </div>
  );
};

export default ShipToFreightSettings;
